import React, { useState, useEffect, useRef } from 'react';
import { Edit2, Trash2, ChevronDown, Trophy } from 'lucide-react';
import { Championship } from '../types';
import './ChampionshipList.css';

interface ChampionshipListProps {
  championships: Championship[];
  selectedId: string | null;
  onSelect: (championship: Championship) => void;
  onDelete: (championshipId: string) => void;
}

const ChampionshipList: React.FC<ChampionshipListProps> = ({
  championships,
  selectedId,
  onSelect,
  onDelete,
}) => {
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'season' | 'events'>('name');
  const [sortOpen, setSortOpen] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setSortOpen(false);
      }
    };

    if (sortOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [sortOpen]);

  // Filter and sort championships
  const filteredChampionships = championships
    .filter((championship) =>
      championship.name.toLowerCase().includes(searchText.toLowerCase()) ||
      championship.communityAbbreviation.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'season') return (b.season || 0) - (a.season || 0);
      if (sortBy === 'events') return (b.events?.length || 0) - (a.events?.length || 0);
      return 0;
    });

  return (
    <div className="championship-list">
      <div className="list-controls">
        <input
          type="text"
          placeholder="Search championships..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="search-input"
        />
        <div className="sort-dropdown" ref={sortDropdownRef}>
          <button
            className="sort-button"
            onClick={() => setSortOpen(!sortOpen)}
            title="Sort championships"
          >
            <ChevronDown size={16} />
          </button>
          {sortOpen && (
            <div className="sort-menu">
              <button
                className={`sort-option ${sortBy === 'name' ? 'active' : ''}`}
                onClick={() => {
                  setSortBy('name');
                  setSortOpen(false);
                }}
              >
                Sort by Name
              </button>
              <button
                className={`sort-option ${sortBy === 'season' ? 'active' : ''}`}
                onClick={() => {
                  setSortBy('season');
                  setSortOpen(false);
                }}
              >
                Sort by Season
              </button>
              <button
                className={`sort-option ${sortBy === 'events' ? 'active' : ''}`}
                onClick={() => {
                  setSortBy('events');
                  setSortOpen(false);
                }}
              >
                Sort by Event Count
              </button>
            </div>
          )}
        </div>
      </div>
      {filteredChampionships.length === 0 ? (
        <div className="no-championships">
          {searchText ? 'No championships match search' : 'No championships configured yet'}
        </div>
      ) : (
        filteredChampionships.map((championship) => {
          const totalRounds = championship.events?.reduce((sum, event) => sum + (event.rounds?.length || 0), 0) || 0;

          return (
            <div
              key={championship.id}
              className={`championship-item ${selectedId === championship.id ? 'active' : ''}`}
              onClick={() => onSelect(championship)}
            >
              <div className="championship-info">
                <h3>
                  <Trophy size={16} />
                  {championship.name}
                </h3>
                <div className="championship-meta">
                  <span className="championship-abbr">{championship.recurrence}</span>
                  <span className="separator">•</span>
                  <span className="championship-season">Season {championship.season}</span>
                </div>
                <div className="championship-stats">
                  <span>{championship.events?.length || 0} event{championship.events?.length !== 1 ? 's' : ''}</span>
                  <span className="separator">•</span>
                  <span>{totalRounds} round{totalRounds !== 1 ? 's' : ''}</span>
                  <span className="separator">•</span>
                  <span>{championship.spots} spot{championship.spots !== 1 ? 's' : ''}</span>
                </div>
                {championship.enableRegistrations && (
                  <div className="championship-badge registrations">
                    Registrations Open
                  </div>
                )}
              </div>
              <div
                className="championship-actions"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="action-button edit"
                  onClick={() => onSelect(championship)}
                  title="Edit championship"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  className="action-button delete"
                  onClick={() => onDelete(championship.id)}
                  title="Delete championship"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ChampionshipList;
